'use client';

import { DropdownMenu } from '@common/components';
import { Pagination } from '@common/components/molecule/Pagination';

import type { PolicySortOption } from '../../_hooks/usePolicyFilter';
import { EMPTY_SEARCH_RESULT_MESSAGE, PAGE_SIZE } from '../../constants';
import type { PolicyData } from './SearchPolicyCard';
import { SearchPolicyCard } from './SearchPolicyCard';
import { SearchPolicyCardSkeletonList } from './SearchPolicyCardSkeleton';

const SORT_LABEL: Record<PolicySortOption, string> = {
  POPULARITY: '추천순',
  LATEST: '최신순',
};

const SORT_OPTIONS = (Object.keys(SORT_LABEL) as PolicySortOption[]).map(
  (value) => ({ value, label: SORT_LABEL[value] }),
);

interface PolicyResultListProps {
  policies: PolicyData[];
  totalCount: number;
  sort: PolicySortOption;
  onSortChange: (sort: PolicySortOption) => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  emptyMessage?: string;
}

export function PolicyResultList({
  policies,
  totalCount,
  sort,
  onSortChange,
  page,
  totalPages,
  onPageChange,
  isLoading = false,
  emptyMessage = EMPTY_SEARCH_RESULT_MESSAGE,
}: PolicyResultListProps) {
  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="flex items-center justify-between mobile-tablet:px-20">
        <p className="font-700 tracking-[-0.28px] text-Gray-black typo-body4 mobile-tablet:font-500">
          전체 {totalCount.toLocaleString()}건
        </p>
        <DropdownMenu
          type="text"
          align="end"
          options={SORT_OPTIONS}
          value={sort}
          onChange={onSortChange}
          contentClassName="min-w-150"
        />
      </div>

      <div className="flex flex-1 flex-col">
        {isLoading ? (
          <SearchPolicyCardSkeletonList count={PAGE_SIZE} />
        ) : policies.length > 0 ? (
          <div className="flex flex-col gap-12 pb-20 mobile-tablet:gap-0 mobile-tablet:pb-0">
            {policies.map((policy) => (
              <SearchPolicyCard key={policy.id} policy={policy} />
            ))}
          </div>
        ) : (
          <div className="flex-1 py-80 flex-center">
            <p className="text-Gray-550 typo-body4">{emptyMessage}</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <Pagination
          totalCount={totalCount}
          currentPage={page}
          size={PAGE_SIZE}
          onMovePage={onPageChange}
          perButtonCount={5}
          className="my-0 pb-40"
        />
      )}
    </div>
  );
}
